<div class="page student-profile-page">
  <header class="profile-topbar">
    <nav class="breadcrumb" [attr.aria-label]="'COMMON.BREADCRUMB' | translate">
      <a [routerLink]="studentsLink" class="breadcrumb-link">{{ 'STUDENTS.TITLE' | translate }}</a>
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <span class="breadcrumb-current">{{ nameAr || ('STUDENTS.PROFILE' | translate) }}</span>
    </nav>

    <div class="topbar-actions">
      <button type="button" class="btn btn-secondary" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ 'COMMON.BACK' | translate }}
      </button>
      <ng-container *appAccess="'students:edit'">
        <button type="button" class="btn btn-primary" *ngIf="student" (click)="onEdit()">
          <mat-icon>edit</mat-icon>
          {{ 'COMMON.EDIT' | translate }}
        </button>
      </ng-container>
    </div>
  </header>

  <div class="alert alert-error" *ngIf="errorMessage">{{ errorMessage | translate }}</div>

  <div class="profile-loading" *ngIf="loading">
    <div class="skeleton-photo"></div>
    <div class="skeleton-copy">
      <span class="skeleton-line wide"></span>
      <span class="skeleton-line"></span>
      <span class="skeleton-line short"></span>
    </div>
  </div>

  <div class="student-profile" *ngIf="!loading && student">
    <section class="identity-card">
      <button
        type="button"
        class="avatar-btn"
        [class.has-photo]="hasPhoto"
        [disabled]="!hasPhoto"
        (click)="openPhoto()"
        [attr.aria-label]="(hasPhoto ? 'STUDENTS.VIEW_PHOTO' : 'STUDENTS.NO_PHOTO') | translate"
      >
        <img *ngIf="hasPhoto" [src]="student.photo" [alt]="nameAr" (error)="onPhotoError()" />
        <span class="avatar-fallback" *ngIf="!hasPhoto" aria-hidden="true">
          <mat-icon>person</mat-icon>
        </span>
        <span class="avatar-zoom" *ngIf="hasPhoto">
          <mat-icon>zoom_in</mat-icon>
        </span>
      </button>

      <div class="identity-copy">
        <div class="identity-meta">
          <span class="status-pill" [attr.data-status]="student.status || 'active'">
            <span class="status-dot"></span>
            {{ statusKey | translate }}
          </span>
          <span class="meta-sep" *ngIf="student.studentCode">·</span>
          <span class="student-code" *ngIf="student.studentCode">{{ student.studentCode }}</span>
        </div>

        <h1 class="name-ar">{{ nameAr }}</h1>
        <p class="name-en" *ngIf="nameEn">{{ nameEn }}</p>

        <ul class="identity-tags">
          <li *ngIf="student.class?.name">
            <mat-icon>class</mat-icon>
            {{ student.class.name }}
          </li>
          <li *ngIf="sexKey">
            <mat-icon>wc</mat-icon>
            {{ sexKey | translate }}
          </li>
          <li *ngIf="age !== null">
            <mat-icon>cake</mat-icon>
            {{ 'STUDENTS.AGE_YEARS' | translate: { count: age } }}
          </li>
          <li *ngIf="student.bloodType">
            <mat-icon>bloodtype</mat-icon>
            {{ student.bloodType }}
          </li>
        </ul>
      </div>

      <aside class="student-qr" *ngIf="qrDataUrl">
        <img [src]="qrDataUrl" [alt]="'STUDENTS.QR_CODE' | translate" />
      </aside>
    </section>

    <section class="metric-strip" [attr.aria-label]="'STUDENTS.PROFILE' | translate">
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.CLASS' | translate }}</span>
        <strong>{{ valueOrDash(student.class?.name) }}</strong>
        <small>{{ valueOrDash(student.class?.major?.name) }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.BUS' | translate }}</span>
        <strong>{{ valueOrDash(student.bus?.number || student.bus?.name) }}</strong>
        <small>{{ valueOrDash(student.bus?.driverName) }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.PRIMARY_EMERGENCY' | translate }}</span>
        <strong>{{ emergencyContactName }}</strong>
        <small>{{ valueOrDash(emergencyPhone) }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.AREA' | translate }}</span>
        <strong>{{ valueOrDash(student.address?.area || student.address?.line1) }}</strong>
        <small>{{ valueOrDash(student.address?.emirate) }}</small>
      </div>
    </section>

    <nav class="profile-tabs" role="tablist" [attr.aria-label]="'STUDENTS.PROFILE' | translate">
      <button
        type="button"
        class="profile-tab"
        *ngFor="let tab of tabs"
        role="tab"
        [id]="'student-tab-' + tab.id"
        [attr.aria-selected]="activeTab === tab.id"
        [attr.aria-controls]="'student-panel-' + tab.id"
        [class.active]="activeTab === tab.id"
        (click)="setTab(tab.id)"
      >
        <mat-icon>{{ tab.icon }}</mat-icon>
        {{ tab.labelKey | translate }}
      </button>
    </nav>

    <div
      class="tab-panel"
      id="student-panel-overview"
      role="tabpanel"
      aria-labelledby="student-tab-overview"
      *ngIf="activeTab === 'overview'"
    >
    <div class="profile-body">
      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_IDENTITY' | translate }}</h2>
        </header>
        <dl class="fields">
          <div class="field">
            <dt>{{ 'STUDENTS.FIRST_NAME_AR' | translate }}</dt>
            <dd>{{ valueOrDash(student.firstNameAr) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.LAST_NAME_AR' | translate }}</dt>
            <dd>{{ valueOrDash(student.lastNameAr) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.FIRST_NAME_EN' | translate }}</dt>
            <dd>{{ valueOrDash(student.firstNameEn) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.LAST_NAME_EN' | translate }}</dt>
            <dd>{{ valueOrDash(student.lastNameEn) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.NATIONAL_ID' | translate }}</dt>
            <dd class="mono">{{ valueOrDash(student.nationalId) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.DATE_OF_BIRTH' | translate }}</dt>
            <dd>
              {{ valueOrDash(student.dateOfBirth) }}
              <span class="field-hint" *ngIf="age !== null">{{ 'STUDENTS.AGE_YEARS' | translate: { count: age } }}</span>
            </dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.NATIONALITY' | translate }}</dt>
            <dd>{{ bilingualLabel(student.nationality) || '—' }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.RELIGION' | translate }}</dt>
            <dd>{{ bilingualLabel(student.religion) || '—' }}</dd>
          </div>
        </dl>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_SCHOOL' | translate }}</h2>
        </header>
        <dl class="fields">
          <div class="field">
            <dt>{{ 'STUDENTS.CLASS' | translate }}</dt>
            <dd>{{ valueOrDash(student.class?.name) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'MAJORS.TITLE' | translate }}</dt>
            <dd>{{ valueOrDash(student.class?.major?.name) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.ACADEMIC_YEAR' | translate }}</dt>
            <dd>{{ bilingualLabel(student.academicYear) || '—' }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.ENROLLMENT_DATE' | translate }}</dt>
            <dd>{{ valueOrDash(student.enrollmentDate) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.BUS' | translate }}</dt>
            <dd>{{ valueOrDash(student.bus?.number || student.bus?.name) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'BUSES.DRIVER' | translate }}</dt>
            <dd>{{ valueOrDash(student.bus?.driverName) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'BUSES.DRIVER_PHONE' | translate }}</dt>
            <dd>
              <a *ngIf="student.bus?.driverPhone; else dash" class="inline-link" [href]="'tel:' + student.bus.driverPhone">
                {{ student.bus.driverPhone }}
              </a>
              <ng-template #dash>—</ng-template>
            </dd>
          </div>
          <div class="field">
            <dt>{{ 'BUSES.PLATE' | translate }}</dt>
            <dd>{{ valueOrDash(student.bus?.plateNumber) }}</dd>
          </div>
        </dl>
      </section>

      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_PARENTS' | translate }}</h2>
          <span class="count" *ngIf="parents.length">{{ parents.length }}</span>
        </header>

        <div class="people-list" *ngIf="parents.length; else noParents">
          <article
            class="person-row"
            *ngFor="let link of parents"
            [class.clickable]="!!link.parent?.id && permissions.canView('parents')"
            [class.is-emergency]="link.isPrimaryEmergency"
            (click)="openParent(link)"
          >
            <div class="person-avatar" aria-hidden="true">{{ parentInitial(link) }}</div>
            <div class="person-copy">
              <h3>{{ parentNameAr(link) }}</h3>
              <p class="person-en" *ngIf="parentNameEn(link)">{{ parentNameEn(link) }}</p>
              <p class="person-role">
                {{ relationKey(link.relation) | translate }}
                <span *ngIf="link.isPrimaryEmergency"> · {{ 'STUDENTS.PRIMARY_EMERGENCY' | translate }}</span>
              </p>
            </div>
            <div class="person-actions" (click)="$event.stopPropagation()">
              <a
                *ngFor="let phone of parentPhones(link)"
                class="contact-chip"
                [href]="'tel:' + phone.value"
              >
                <mat-icon>call</mat-icon>
                <span>{{ phone.value }}</span>
              </a>
              <a
                *ngFor="let email of parentEmails(link)"
                class="contact-chip"
                [href]="'mailto:' + email.value"
              >
                <mat-icon>mail</mat-icon>
                <span>{{ email.value }}</span>
              </a>
              <span class="empty-inline" *ngIf="!parentPhones(link).length && !parentEmails(link).length">
                {{ 'STUDENTS.NO_PHONES' | translate }}
              </span>
            </div>
            <mat-icon class="row-chevron" *ngIf="link.parent?.id">chevron_right</mat-icon>
          </article>
        </div>
        <ng-template #noParents>
          <p class="empty">{{ 'STUDENTS.NO_PARENTS' | translate }}</p>
        </ng-template>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_PHONES' | translate }}</h2>
        </header>
        <div class="contact-stack" *ngIf="phones.length; else noPhones">
          <a class="contact-line" *ngFor="let phone of phones" [href]="'tel:' + phone.value">
            <mat-icon>smartphone</mat-icon>
            <span>
              <strong>{{ phone.value }}</strong>
              <small>{{ labelKey(phone.label) | translate }}<ng-container *ngIf="phone.isPrimary"> · {{ 'STUDENTS.PRIMARY' | translate }}</ng-container></small>
            </span>
          </a>
        </div>
        <ng-template #noPhones>
          <p class="empty">{{ 'STUDENTS.NO_PHONES' | translate }}</p>
        </ng-template>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_EMAILS' | translate }}</h2>
        </header>
        <div class="contact-stack" *ngIf="emails.length; else noEmails">
          <a class="contact-line" *ngFor="let email of emails" [href]="'mailto:' + email.value">
            <mat-icon>alternate_email</mat-icon>
            <span>
              <strong>{{ email.value }}</strong>
              <small>{{ labelKey(email.label) | translate }}<ng-container *ngIf="email.isPrimary"> · {{ 'STUDENTS.PRIMARY' | translate }}</ng-container></small>
            </span>
          </a>
        </div>
        <ng-template #noEmails>
          <p class="empty">{{ 'STUDENTS.NO_EMAILS' | translate }}</p>
        </ng-template>
      </section>

      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.SECTION_ADDRESS' | translate }}</h2>
          <a
            *ngIf="mapsLink"
            class="btn btn-secondary btn-sm"
            [href]="mapsLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            <mat-icon>open_in_new</mat-icon>
            {{ 'STUDENTS.OPEN_MAPS' | translate }}
          </a>
        </header>

        <div class="address-grid" *ngIf="hasAddress; else noAddress">
          <dl class="fields">
            <div class="field">
              <dt>{{ 'STUDENTS.EMIRATE' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.emirate) }}</dd>
            </div>
            <div class="field">
              <dt>{{ 'STUDENTS.AREA' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.area) }}</dd>
            </div>
            <div class="field">
              <dt>{{ 'STUDENTS.LINE1' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.line1) }}</dd>
            </div>
            <div class="field">
              <dt>{{ 'STUDENTS.LINE2' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.line2) }}</dd>
            </div>
            <div class="field">
              <dt>{{ 'STUDENTS.BUILDING' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.building) }}</dd>
            </div>
            <div class="field">
              <dt>{{ 'STUDENTS.FLAT' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.flat) }}</dd>
            </div>
            <div class="field field-span">
              <dt>{{ 'STUDENTS.LANDMARK' | translate }}</dt>
              <dd>{{ valueOrDash(student.address?.landmark) }}</dd>
            </div>
          </dl>
          <div class="map-wrap" *ngIf="hasCoordinates">
            <div #mapHost class="map-host" role="img" [attr.aria-label]="'STUDENTS.MAP_TITLE' | translate"></div>
          </div>
        </div>
        <ng-template #noAddress>
          <p class="empty">{{ 'STUDENTS.NO_ADDRESS' | translate }}</p>
        </ng-template>
      </section>
    </div>
    </div>

    <div
      class="tab-panel"
      id="student-panel-teachers"
      role="tabpanel"
      aria-labelledby="student-tab-teachers"
      *ngIf="activeTab === 'teachers'"
    >
      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.TAB_TEACHERS' | translate }}</h2>
          <span class="count" *ngIf="classTeachers.length">{{ classTeachers.length }}</span>
        </header>
        <p class="empty" *ngIf="relatedLoading">{{ 'COMMON.LOADING' | translate }}</p>
        <p class="empty" *ngIf="!relatedLoading && !classId">{{ 'STUDENTS.NO_CLASS' | translate }}</p>
        <p class="empty" *ngIf="!relatedLoading && classId && !classTeachers.length">{{ 'STUDENTS.NO_TEACHERS' | translate }}</p>
        <div class="people-list" *ngIf="!relatedLoading && classTeachers.length">
          <article class="person-row" [class.clickable]="permissions.canView('users')" *ngFor="let teacher of classTeachers" (click)="openTeacher(teacher)">
            <div class="person-avatar" aria-hidden="true">{{ teacherName(teacher).slice(0, 1) }}</div>
            <div class="person-copy">
              <h3>{{ teacherName(teacher) }}</h3>
              <p class="person-role">{{ teacherSubjects(teacher) }}</p>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div
      class="tab-panel"
      id="student-panel-subjects"
      role="tabpanel"
      aria-labelledby="student-tab-subjects"
      *ngIf="activeTab === 'subjects'"
    >
      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.TAB_SUBJECTS' | translate }}</h2>
          <span class="count" *ngIf="classSubjects.length">{{ classSubjects.length }}</span>
        </header>
        <p class="empty" *ngIf="relatedLoading">{{ 'COMMON.LOADING' | translate }}</p>
        <p class="empty" *ngIf="!relatedLoading && !classId">{{ 'STUDENTS.NO_CLASS' | translate }}</p>
        <p class="empty" *ngIf="!relatedLoading && classId && !classSubjects.length">{{ 'STUDENTS.NO_SUBJECTS' | translate }}</p>
        <div class="people-list" *ngIf="!relatedLoading && classSubjects.length">
          <article class="person-row" *ngFor="let subject of classSubjects">
            <div class="person-avatar subject-avatar" aria-hidden="true">
              <mat-icon>menu_book</mat-icon>
            </div>
            <div class="person-copy">
              <h3>{{ subject.name }}</h3>
              <p class="person-role">
                {{ subject.teachers.length }}
                {{ 'STUDENTS.TAB_TEACHERS' | translate }}
                · {{ subjectTeachersLabel(subject) }}
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div
      class="tab-panel"
      id="student-panel-weekly-plan"
      role="tabpanel"
      aria-labelledby="student-tab-weekly-plan"
      *ngIf="activeTab === 'weekly-plan'"
    >
      <section class="panel panel-wide panel-plan">
        <header class="panel-head">
          <h2>{{ 'STUDENTS.TAB_WEEKLY_PLAN' | translate }}</h2>
          <div class="panel-tools">
            <label class="week-select">
              <span>{{ 'WEEKS.WEEK' | translate }}</span>
              <select
                [ngModel]="selectedWeekOrdinal"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onWeekChange($event)"
                [disabled]="!weeks.length || planLoading"
              >
                <option *ngFor="let week of weeks" [ngValue]="week.ordinal">
                  {{ week.name }}
                </option>
              </select>
            </label>
          </div>
        </header>
        <p class="empty" *ngIf="planLoading || relatedLoading">{{ 'COMMON.LOADING' | translate }}</p>
        <p class="empty" *ngIf="!planLoading && !relatedLoading && !classId">{{ 'STUDENTS.NO_CLASS' | translate }}</p>
        <ng-container *ngIf="!planLoading && !relatedLoading && classId">
          <p class="empty" *ngIf="!weeklyPlan">{{ 'STUDENTS.NO_WEEKLY_PLAN' | translate }}</p>
          <p class="empty" *ngIf="weeklyPlan && !planTables.length">{{ 'STUDENTS.NO_PLAN_ENTRIES' | translate }}</p>
          <div class="student-plan-tables" dir="rtl" *ngIf="planTables.length">
            <article class="wp-subject-card" *ngFor="let entry of planTables">
              <div class="wp-card-head">
                <div class="wp-card-head-main">
                  <strong class="wp-subject-name">{{ entry.subjectName }}</strong>
                  <span class="wp-teacher-label" *ngIf="entry.teacherName">
                    {{ 'PLAN.TEACHER_LABEL' | translate }}: {{ entry.teacherName }}
                  </span>
                </div>
              </div>
              <div class="wp-capture-area">
                <div class="wp-capture-meta">
                  <div class="wp-capture-meta-top">
                    <div class="wp-capture-meta-main">
                      <div class="wp-meta-subject">{{ entry.subjectName }}</div>
                      <div class="wp-meta-line"><b>{{ planSchoolName }}</b> — {{ planDepartmentName }}</div>
                    </div>
                    <div class="wp-capture-logos">
                      <img src="assets/weekly-plan/school-logo.png" alt="شعار المدرسة">
                      <img src="assets/weekly-plan/year-banner.jpg" alt="بالميثاق نبلغ الآفاق">
                    </div>
                  </div>
                  <div class="wp-capture-meta-bottom">
                    <div>{{ planClassName }} · {{ weekChipLabel }}</div>
                    <div>{{ fmtDisplayDate(weeklyPlan?.startDate) }} إلى {{ fmtDisplayDate(weeklyPlan?.endDate) }}</div>
                  </div>
                </div>
                <div class="wp-table-scroll">
                  <table class="wp-plan">
                    <thead>
                      <tr>
                        <th>العنصر</th>
                        <th *ngFor="let day of planDays">{{ day }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of planRows; let rowIndex = index">
                        <th>{{ row.label }}</th>
                        <td *ngFor="let day of planDays; let dayIndex = index">
                          <div class="wp-cell-text">{{ cellContent(entry.grid, rowIndex, dayIndex) }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="wp-teacher-line" *ngIf="entry.teacherName">
                  معلم المادة: {{ entry.teacherName }}
                </div>
              </div>
            </article>
          </div>
        </ng-container>
      </section>
    </div>
  </div>
</div>

<div class="photo-lightbox" *ngIf="photoLightboxOpen && hasPhoto" (click)="closePhoto()">
  <button type="button" class="lightbox-close" (click)="closePhoto()" [attr.aria-label]="'COMMON.CLOSE' | translate">
    <mat-icon>close</mat-icon>
  </button>
  <img [src]="student.photo" [alt]="nameAr" (click)="$event.stopPropagation()" />
</div>
